/**
 * Utility functions for exporting data to CSV format
 */

/**
 * Escapes a CSV field value by wrapping it in quotes if it contains special characters
 * @param value - The value to escape
 * @returns Escaped CSV field value
 */
export const escapeCsvField = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) {
    return '';
  }

  const stringValue = String(value);
  
  // If the value contains comma, newline, or double quote, wrap it in quotes
  if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
    // Escape double quotes by doubling them
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  
  return stringValue;
};

/**
 * Converts an array of objects to CSV format
 * @param data - Array of objects to convert
 * @param headers - Array of header objects with key and label properties
 * @returns CSV string
 */
export const convertToCSV = <T extends Record<string, any>>(
  data: T[],
  headers: Array<{ key: keyof T; label: string }>
): string => {
  if (!data || data.length === 0) {
    return headers.map(h => escapeCsvField(h.label)).join(',');
  }

  // Create header row
  const headerRow = headers.map(h => escapeCsvField(h.label)).join(',');

  // Create data rows
  const dataRows = data.map(item => 
    headers.map(h => escapeCsvField(item[h.key])).join(',')
  );

  return [headerRow, ...dataRows].join('\n');
};

/**
 * Triggers a browser download of CSV content
 * @param csvContent - The CSV content as a string
 * @param filename - The desired filename (without extension)
 */
export const downloadCSV = (csvContent: string, filename: string): void => {
  // Create a Blob from the CSV content
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  
  // Create a temporary URL for the blob
  const url = URL.createObjectURL(blob);
  
  // Create a temporary anchor element and trigger download
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.csv`;
  link.style.display = 'none';
  
  // Append to body, click, and cleanup
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Release the blob URL
  URL.revokeObjectURL(url);
};
